import { isValidDomain } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconCloudLockOpen, IconMail } from "@tabler/icons-react";
import type { FC } from "react";
import { EmailMagicLinkForm } from "./emailMagicLinkForm.js";
import {
  GoogleSignInButton,
  MicrosoftSignInButton,
} from "./oauthProviderButtons.js";
import { SignInPageShell } from "./signInPageShell.js";

export const SALES_DEMO_LINK =
  "https://cal.frontapp.com/alpharun/paul/41a63af7";

export const InstructionVariant = {
  STANDARD: "standard",
  INSIGHTS_WITH_DOMAIN_LOGIN: "insights_with_domain_login",
  INSIGHTS_WITHOUT_DOMAIN_LOGIN: "insights_without_domain_login",
} as const;
export type InstructionVariant =
  (typeof InstructionVariant)[keyof typeof InstructionVariant];

export const SignInMode = {
  OAUTH: "oauth",
  SSO: "sso",
  EMAIL: "email",
} as const;
export type SignInMode = (typeof SignInMode)[keyof typeof SignInMode];

export interface SignInScreenProps {
  instructionVariant: InstructionVariant;
  mode: SignInMode;
  /** Whether the login-instruction lookup is still in flight (shows skeleton). */
  isLoadingInstructions: boolean;
  /** Interviewee name for insights deep links, if any. */
  interviewContactName: string | null;
  ssoDomain: string;
  isSsoDomainReadOnly: boolean;
  email: string;
  isSendingMagicLink: boolean;
  magicLinkSentToEmail: string | null;
  errorMessage: string | null;
  onModeChange: (mode: SignInMode) => void;
  onSsoDomainChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onGoogleSignIn: () => void;
  onMicrosoftSignIn: () => void;
  onSsoSignIn: () => void;
  onEmailSignIn: () => void;
  onResetMagicLink: () => void;
}

/**
 * Presentational sign-in screen. Renders every mode (OAuth / SSO / email) and
 * instruction variant from props — all auth side effects (Supabase, popups,
 * redirects) are handled by the `SignInPage` container, so this renders
 * standalone in Storybook.
 */
export const SignInScreen: FC<SignInScreenProps> = ({
  instructionVariant,
  mode,
  isLoadingInstructions,
  interviewContactName,
  ssoDomain,
  isSsoDomainReadOnly,
  email,
  isSendingMagicLink,
  magicLinkSentToEmail,
  errorMessage,
  onModeChange,
  onSsoDomainChange,
  onEmailChange,
  onGoogleSignIn,
  onMicrosoftSignIn,
  onSsoSignIn,
  onEmailSignIn,
  onResetMagicLink,
}) => {
  const insightsActionText = interviewContactName
    ? `view the full interview with ${interviewContactName}.`
    : "view interview insights.";

  const computeSubtitle = () => {
    switch (instructionVariant) {
      case InstructionVariant.INSIGHTS_WITH_DOMAIN_LOGIN:
        return `Sign in to ${insightsActionText}`;
      case InstructionVariant.INSIGHTS_WITHOUT_DOMAIN_LOGIN:
        return `Please sign in to ${insightsActionText}`;
      default:
        return "Please sign in to continue.";
    }
  };

  const maybeRenderBottomMessage = () => {
    if (mode !== SignInMode.OAUTH) return null;

    switch (instructionVariant) {
      case InstructionVariant.INSIGHTS_WITH_DOMAIN_LOGIN:
        return (
          <div className="mt-4 text-center text-sm text-gray-600">
            Don&apos;t have an account yet? Sign in with your work email to join
            your team. Accounts for teammates are free.
          </div>
        );
      case InstructionVariant.INSIGHTS_WITHOUT_DOMAIN_LOGIN:
        return (
          <div className="mt-4 text-center text-sm text-gray-600">
            Don&apos;t have an account yet? Request one from your Alpharun
            admin. Accounts for teammates are free.
          </div>
        );
      default:
        return (
          <p className="mt-4 text-center text-sm text-gray-600">
            {"Don't have an account yet? "}
            <a
              href={SALES_DEMO_LINK}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Book a demo.
            </a>
          </p>
        );
    }
  };

  // The back link below the card returns from the SSO/email entry forms to the
  // main screen of sign-in options.
  const shouldShowBackLink =
    !isLoadingInstructions &&
    (mode === SignInMode.SSO ||
      (mode === SignInMode.EMAIL && !magicLinkSentToEmail));

  const renderSigninOptions = () => {
    if (mode === SignInMode.SSO) {
      return (
        <div className="w-full">
          <Input
            label="Company domain"
            placeholder="company.com"
            value={ssoDomain}
            readOnly={isSsoDomainReadOnly}
            inputClassName={isSsoDomainReadOnly ? "bg-gray-100" : undefined}
            onChange={(e) => onSsoDomainChange(e.target.value)}
            maxLength={100}
          />
          {errorMessage ? (
            <p className="mt-2 text-sm text-destructive">{errorMessage}</p>
          ) : null}
          <Button
            className="mx-auto mt-4"
            onClick={onSsoSignIn}
            variant={ButtonVariant.Primary}
            isDisabled={!isValidDomain(ssoDomain)}
          >
            Sign in with SSO
          </Button>
        </div>
      );
    }

    if (mode === SignInMode.EMAIL) {
      return (
        <EmailMagicLinkForm
          email={email}
          isSending={isSendingMagicLink}
          sentToEmail={magicLinkSentToEmail}
          errorMessage={errorMessage}
          onEmailChange={onEmailChange}
          onSubmit={onEmailSignIn}
          onReset={onResetMagicLink}
        />
      );
    }

    return (
      <>
        <GoogleSignInButton
          label="Sign in with Google"
          onClick={onGoogleSignIn}
        />

        <MicrosoftSignInButton
          label="Sign in with Microsoft"
          onClick={onMicrosoftSignIn}
        />

        <Button
          className="w-52"
          onClick={() => onModeChange(SignInMode.SSO)}
          variant={ButtonVariant.Secondary}
        >
          <div className="grid grid-cols-[auto_1fr] justify-center gap-2">
            <IconCloudLockOpen className="size-5 shrink-0" />
            <span>Sign in with SSO</span>
          </div>
        </Button>

        <Button
          className="w-52"
          onClick={() => onModeChange(SignInMode.EMAIL)}
          variant={ButtonVariant.Secondary}
        >
          <div className="grid grid-cols-[auto_1fr] justify-center gap-2">
            <IconMail className="size-5 shrink-0" />
            <span>Sign in with email</span>
          </div>
        </Button>
      </>
    );
  };

  return (
    <SignInPageShell
      belowCard={
        shouldShowBackLink ? (
          <div className="mt-4 text-center">
            <button
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
              onClick={() => onModeChange(SignInMode.OAUTH)}
            >
              Back
            </button>
          </div>
        ) : null
      }
    >
      {isLoadingInstructions ? (
        <DefaultSkeleton />
      ) : (
        <>
          <h2 className="mb-2 text-center text-2xl font-semibold text-foreground">
            Welcome
          </h2>
          <div className="mb-4 text-center text-sm text-gray-600">
            {computeSubtitle()}
          </div>
          <div className="flex flex-col items-center space-y-4">
            {renderSigninOptions()}
          </div>
          {maybeRenderBottomMessage()}
          {mode === SignInMode.OAUTH && errorMessage ? (
            <p className="mt-4 text-center text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}
        </>
      )}
    </SignInPageShell>
  );
};
